import React from 'react';
import { QprSectionData, QprGoalReviewItem, Client, ClientGoal } from '../../types';
import {
  Calendar,
  Target,
  ShieldCheck,
  Award,
  HeartHandshake,
  UserCheck,
  Trash2,
  FileText,
  Clock,
  TrendingUp,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface QprWorkspaceProps {
  data: QprSectionData;
  onChange: (updated: QprSectionData) => void;
  client?: Client;
  canonicalGoals?: ClientGoal[];
  readOnly?: boolean;
}

export const QprWorkspace: React.FC<QprWorkspaceProps> = ({
  data,
  onChange,
  client,
  canonicalGoals = [],
  readOnly = false
}) => {
  const updateField = <K extends keyof QprSectionData>(field: K, value: QprSectionData[K]) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const attendance = data.attendance || {
    completed_sessions: 0,
    cancelled_sessions: 0,
    no_show_sessions: 0,
    cancellation_reasons: '',
    service_interruptions: ''
  };

  const updateAttendance = (field: string, val: any) => {
    updateField('attendance', {
      ...attendance,
      [field]: val
    });
  };

  // Goals Review
  const goalsReview: QprGoalReviewItem[] = data.goals_review || (canonicalGoals.length > 0
    ? canonicalGoals.map(cg => ({
        goal_id: cg.id,
        description: cg.description,
        baseline: cg.baseline_performance || undefined,
        target_percentage: typeof cg.target_percentage === 'number' ? cg.target_percentage : undefined,
        current_accuracy: typeof cg.current_percentage === 'number' ? cg.current_percentage : undefined
      }))
    : []);

  const addGoalReview = () => {
    updateField('goals_review', [
      ...goalsReview,
      {
        description: ''
      }
    ]);
  };

  const removeGoalReview = (idx: number) => {
    updateField('goals_review', goalsReview.filter((_, i) => i !== idx));
  };

  const updateGoalReview = (idx: number, field: keyof QprGoalReviewItem, val: any) => {
    const updated = [...goalsReview];
    updated[idx] = { ...updated[idx], [field]: val };
    updateField('goals_review', updated);
  };

  const homeOversight = data.home_program_oversight || {
    clinician_oversight_notes: ''
  };

  return (
    <div className="space-y-6 text-slate-200">
      {/* 1. Service Period & Attendance Accounting */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Calendar className="w-4 h-4 text-sky-400" />
          <span>1. Reporting Period & Attendance Record (DDD-2063A Requirement)</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Reporting Quarter Label
            </label>
            <input
              type="text"
              disabled={readOnly}
              value={data.reporting_period_label || 'Q1 2026 (Jan 1 - Mar 31)'}
              onChange={(e) => updateField('reporting_period_label', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Quarter Start Date
            </label>
            <input
              type="date"
              disabled={readOnly}
              value={data.reporting_period_start || '2026-01-01'}
              onChange={(e) => updateField('reporting_period_start', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Quarter End Date
            </label>
            <input
              type="date"
              disabled={readOnly}
              value={data.reporting_period_end || '2026-03-31'}
              onChange={(e) => updateField('reporting_period_end', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>

        <div className="p-3 bg-[#121c2e] rounded-lg border border-slate-700/70 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Sessions Completed</label>
            <input
              type="number"
              disabled={readOnly}
              value={attendance.completed_sessions ?? 24}
              onChange={(e) => updateAttendance('completed_sessions', Number(e.target.value))}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2 py-1 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Cancelled by Family</label>
            <input
              type="number"
              disabled={readOnly}
              value={attendance.cancelled_sessions ?? 0}
              onChange={(e) => updateAttendance('cancelled_sessions', Number(e.target.value))}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2 py-1 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">No Shows</label>
            <input
              type="number"
              disabled={readOnly}
              value={attendance.no_show_sessions ?? 0}
              onChange={(e) => updateAttendance('no_show_sessions', Number(e.target.value))}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2 py-1 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-200 mb-1">Cancellation Reasons</label>
            <input
              type="text"
              disabled={readOnly}
              value={attendance.cancellation_reasons || ''}
              onChange={(e) => updateAttendance('cancellation_reasons', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 2. Collaboration & Medical Updates */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <HeartHandshake className="w-4 h-4 text-sky-400" />
          <span>2. Collaboration, Coordination & Medical Updates</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Interdisciplinary Collaboration (Teachers, Support Coordinator, OT/PT)
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.collaboration_summary || ''}
              onChange={(e) => updateField('collaboration_summary', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Medical & Environmental Changes During Quarter
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.medical_updates || ''}
              onChange={(e) => updateField('medical_updates', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 3. Summary of Therapy & Skilled Care Justification */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-amber-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <ShieldCheck className="w-4 h-4 text-amber-400" />
          <span>3. Therapy Summary & Skilled Care Justification</span>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-amber-200 mb-1">
            Clinical Summary of Quarterly Interventions & Progress
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.therapy_summary || ''}
            onChange={(e) => updateField('therapy_summary', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-amber-200 mb-1">
            Skilled Need Rationale (Why specialized SLP expertise is required)
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.skilled_care_justification || ''}
            onChange={(e) => updateField('skilled_care_justification', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 focus:outline-none leading-relaxed italic disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>
      </div>

      {/* 4. Goal-by-Goal Performance & Clinical Review */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
          <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
            <Target className="w-4 h-4 text-sky-400" />
            <span>4. Goal-by-Goal Performance & Clinical Review</span>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={addGoalReview}
              className="px-2.5 py-1 rounded-lg bg-sky-600 hover:bg-sky-500 text-white shadow-xs text-xs font-semibold flex items-center transition-colors cursor-pointer shrink-0"
            >
              <span>Add Goal Review</span>
            </button>
          )}
        </div>

        {goalsReview.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center space-x-2 font-semibold">
            <AlertCircle className="w-4 h-4 text-amber-800 shrink-0" />
            <span>Treatment Goal Required</span>
          </div>
        ) : (
          <div className="space-y-4">
            {goalsReview.map((g, idx) => (
            <div key={idx} className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-slate-100 text-xs">Goal #{idx + 1} Review</span>
                {!readOnly && goalsReview.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGoalReview(idx)}
                    className="text-rose-400 hover:text-rose-300 p-1.5 rounded hover:bg-rose-950/40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-200 mb-1">
                  Goal Objective Description
                </label>
                <textarea
                  disabled={readOnly}
                  rows={2}
                  value={g.description}
                  onChange={(e) => updateGoalReview(idx, 'description', e.target.value)}
                  className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Baseline</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={g.baseline || ''}
                    onChange={(e) => updateGoalReview(idx, 'baseline', e.target.value)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Current Accuracy (%)</label>
                  <input
                    type="number"
                    disabled={readOnly}
                    value={typeof g.current_accuracy === 'number' ? g.current_accuracy : ''}
                    onChange={(e) => updateGoalReview(idx, 'current_accuracy', e.target.value === '' ? undefined : Number(e.target.value))}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Quarter Status</label>
                  <select
                    disabled={readOnly}
                    value={g.change_status || ''}
                    onChange={(e) => updateGoalReview(idx, 'change_status', e.target.value || undefined)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  >
                    <option value="">Not assessed</option>
                    <option value="IMPROVED">Improved</option>
                    <option value="MAINTAINED">Maintained</option>
                    <option value="REGRESSED">Regressed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Recommendation</label>
                  <select
                    disabled={readOnly}
                    value={g.recommendation || ''}
                    onChange={(e) => updateGoalReview(idx, 'recommendation', e.target.value || undefined)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  >
                    <option value="">Not assessed</option>
                    <option value="CONTINUE">Continue</option>
                    <option value="MODIFY">Modify / Revise</option>
                    <option value="DISCONTINUE_MET">Discontinue (Met)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Cueing Level & Support</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={g.cueing_level || ''}
                    onChange={(e) => updateGoalReview(idx, 'cueing_level', e.target.value)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Functional Impact in Daily Life</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={g.functional_impact || ''}
                    onChange={(e) => updateGoalReview(idx, 'functional_impact', e.target.value)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* 5. Home Program: Strategies, Progress & Updates */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-4">
        <div className="flex items-center space-x-2 text-indigo-300 font-bold text-xs uppercase tracking-wider pb-2 border-b border-indigo-500/30">
          <HeartHandshake className="w-4 h-4 text-indigo-400" />
          <span>5. Home Program: Strategies, Progress & Updates</span>
        </div>

        {/* Part 1: Functional Strategies */}
        <div className="space-y-1">
          <label className="block text-[10px] font-bold text-indigo-300 uppercase tracking-wider">
            Part A: Functional Strategies Implemented in the Home Environment
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.home_program_strategies || ''}
            onChange={(e) => updateField('home_program_strategies', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>

        {/* Part 2: Progress / Oversight */}
        <div className="space-y-2 p-3 bg-[#121c2e] rounded-lg border border-indigo-500/30">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
            <label className="block text-[10px] font-bold text-indigo-300 uppercase tracking-wider">
              Part B: Progress & Oversight of the Home Program
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full sm:w-auto min-w-0">
              <span className="text-[10px] text-slate-200 font-medium shrink-0">Caregiver Execution:</span>
              <select
                disabled={readOnly}
                value={homeOversight.caregiver_implementation || ''}
                onChange={(e) =>
                  updateField('home_program_oversight', {
                    ...homeOversight,
                    caregiver_implementation: (e.target.value || undefined) as any
                  })
                }
                className="w-full sm:w-auto min-w-0 bg-[#0a101b] border border-slate-700/80 text-slate-100 text-xs rounded px-2.5 py-1 font-bold focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none shadow-inner"
              >
                <option value="">Not assessed</option>
                <option value="CONSISTENT">Consistent Execution</option>
                <option value="VARIABLE">Variable Execution</option>
                <option value="INCONSISTENT">Inconsistent Execution</option>
                <option value="LIMITED">Limited Execution</option>
              </select>
            </div>
          </div>
          <textarea
            disabled={readOnly}
            rows={2}
            value={homeOversight.clinician_oversight_notes || ''}
            onChange={(e) =>
              updateField('home_program_oversight', {
                ...homeOversight,
                clinician_oversight_notes: e.target.value
              })
            }
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>

        {/* Part 3: Changes or Additions for Next Quarter */}
        <div className="space-y-1">
          <label className="block text-[10px] font-bold text-indigo-300 uppercase tracking-wider">
            Part C: Changes, Revisions or Additions to Home Program for Next Quarter
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.home_program_changes || ''}
            onChange={(e) => updateField('home_program_changes', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>
      </div>

      {/* 6. Prognosis & Recommended Dosage */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Award className="w-4 h-4 text-sky-400" />
          <span>6. Prognosis & Recommended Dosage for Upcoming Quarter</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Prognostic Indicators
            </label>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.prognostic_indicators || ''}
              onChange={(e) => updateField('prognostic_indicators', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Recommended Frequency & Duration
            </label>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.recommended_frequency || ''}
              onChange={(e) => updateField('recommended_frequency', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
